import { isNonNullable, isNullable } from '@ipfabric/utils';
import { takeEvery } from 'containers/ErrorBoundary';
import { produce } from 'immer';
import get from 'lodash/get';
import {
  initialLayout,
  normalizeToLayoutSlug,
} from 'pages/Dashboard/Overview/helpers';
import { getSelectedSnapshot } from 'pages/SnapshotManagement/sagas';
import { dissoc } from 'ramda';
import { all, call, put, select } from 'redux-saga/effects';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import { requestMessage } from 'utils/request/requestMessage';
import {
  ADD_WIDGET,
  CLEAR_DASHBOARD,
  CREATE_REPORT_GROUP,
  DELETE_LAYOUT,
  DELETE_REPORT_GROUP,
  FETCH_DASHBOARD,
  FETCH_DISCOVERY_HISTORY,
  FETCH_HOST_VENDOR_MIX,
  FETCH_NETWORK_VENDOR_MIX,
  FETCH_REPORTS,
  FETCH_TEXT_ROW,
  RENAME_LAYOUT,
  SAVE_LAYOUT,
} from './constants';

const clearLayout = (reportsGroups, layout) => {
  if (!layout.rows) {
    return layout;
  }
  const newLayout = layout.rows.reduce((accRows, row, rowIndex) => {
    return row.columns.reduce((accColumns, column, columnIndex) => {
      return column.widgets.reduce((accWidgets, widget) => {
        const group = get(widget, 'props.group');
        if (isNonNullable(group)) {
          const foundIndex = reportsGroups.findIndex((g) => g.id === group);
          if (foundIndex === -1) {
            const widgets = get(accWidgets, [
              'rows',
              rowIndex,
              'columns',
              columnIndex,
              'widgets',
            ]);
            return produce(accWidgets, (draft) => {
              const foundWidgetIndex = widgets.findIndex(
                (w) => get(w, 'props.group') === group,
              );
              if (foundWidgetIndex > -1) {
                draft.rows[rowIndex].columns[columnIndex].widgets.splice(
                  foundWidgetIndex,
                  1,
                );
              }
            });
          }
        }
        return accWidgets;
      }, accColumns);
    }, accRows);
  }, layout);
  return newLayout;
};

const clearLayouts = (reportsGroups, layouts) => {
  return layouts.map((layout) => clearLayout(reportsGroups, layout));
};

const hasWidgetInLayout = (layout, name) => {
  if (!layout.rows) {
    return false;
  }
  return layout.rows.reduce((accRows, row) => {
    return row.columns.reduce((accColumns, column) => {
      return column.widgets.reduce((accWidgets, widget) => {
        if (widget.key === name) {
          return true;
        }
        return accWidgets;
      }, accColumns);
    }, accRows);
  }, false);
};

const hasWidgetInLayouts = (layouts, name) => {
  let hasWidget = false;
  for (const layout of layouts) {
    if (hasWidgetInLayout(layout, name)) {
      hasWidget = true;
      break;
    }
  }
  return hasWidget;
};

function* fetchDiscoveryHistory() {
  const discoveryHistory = yield call(requestForSaga, {
    url: '/discovery/history',
    method: 'GET',
  });
  yield put({
    type: `${FETCH_DISCOVERY_HISTORY}_SUCCESS`,
    payload: { discoveryHistory },
  });
}

function* fetchNetworkVendorMix() {
  const selectedSnapshot = yield getSelectedSnapshot();
  if (!selectedSnapshot) {
    return;
  }
  const networkVendorMix = yield call(requestForSaga, {
    url: `/dashboard/vendors?snapshot=${selectedSnapshot}`,
    method: 'GET',
  });
  yield put({
    type: `${FETCH_NETWORK_VENDOR_MIX}_SUCCESS`,
    payload: { networkVendorMix },
  });
}

function* fetchHostVendorMix() {
  const selectedSnapshot = yield getSelectedSnapshot();
  if (!selectedSnapshot) {
    return;
  }
  const hostVendorMix = yield call(requestForSaga, {
    url: `/dashboard/hosts?snapshot=${selectedSnapshot}`,
    method: 'GET',
  });
  yield put({
    type: `${FETCH_HOST_VENDOR_MIX}_SUCCESS`,
    payload: { hostVendorMix },
  });
}

function* fetchTextRow() {
  const snapshot = yield getSelectedSnapshot();
  if (!snapshot) {
    return;
  }
  const data = yield call(requestForSaga, {
    url: '/tables/management/discovery-runs',
    method: 'POST',
    data: {
      columns: [
        'deviceCount',
        'interfaceCount',
        'interfaceActiveCount',
        'interfaceEdgeCount',
        'userCount',
      ],
      snapshot,
    },
  });
  yield put({
    type: `${FETCH_TEXT_ROW}_SUCCESS`,
    payload: { data },
  });
}

function* fetchDashboardSaga() {
  try {
    yield put({ type: `${FETCH_DASHBOARD}_START` });
    const selectedSnapshot = yield getSelectedSnapshot();
    const [reports, reportsGroups, layouts] = yield all([
      isNullable(selectedSnapshot)
        ? []
        : call(requestForSaga, {
            url: `/reports?snapshot=${selectedSnapshot}`,
            method: 'GET',
          }),
      call(requestForSaga, { url: '/reports/groups', method: 'GET' }),
      call(requestForSaga, { url: '/settings/dashboard', method: 'GET' }),
    ]);

    // We filter out broken layouts that do not have slug or title, and they will not be saved again either.
    const sanitizedLayouts = layouts.filter(
      (x) => x.slug !== undefined && x.title !== undefined,
    );

    const clearedLayouts = clearLayouts(reportsGroups, sanitizedLayouts);

    const layoutsDict = clearedLayouts.reduce((acc, item) => {
      acc[item.slug] = item;
      return acc;
    }, {});

    yield all([
      hasWidgetInLayouts(layouts, 'DeviceChart')
        ? call(fetchDiscoveryHistory)
        : null,
      hasWidgetInLayouts(layouts, 'NetworkVendorMix')
        ? call(fetchNetworkVendorMix)
        : null,
      hasWidgetInLayouts(layouts, 'HostVendorMix')
        ? call(fetchHostVendorMix)
        : null,
      hasWidgetInLayouts(layouts, 'TextRow') ? call(fetchTextRow) : null,
    ]);

    yield put({
      type: `${FETCH_DASHBOARD}_SUCCESS`,
      payload: {
        reports,
        reportsGroups,
        layout: clearedLayouts[0],
        layouts: clearedLayouts,
        layoutsDict: layoutsDict,
      },
    });
  } catch (e) {
    yield put({ type: `${FETCH_DASHBOARD}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* fetchReportsSaga() {
  try {
    const selectedSnapshot = yield getSelectedSnapshot();
    const reports = isNullable(selectedSnapshot)
      ? []
      : yield call(requestForSaga, {
          url: `/reports?snapshot=${selectedSnapshot}`,
          method: 'GET',
        });
    yield put({
      type: `${FETCH_REPORTS}_SUCCESS`,
      payload: {
        reports,
      },
    });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* createReportGroupSaga(action) {
  try {
    const { group } = action.payload;
    yield put({ type: `${CREATE_REPORT_GROUP}_START` });
    const requestGroup = yield call(requestForSaga, {
      url: '/reports/groups',
      method: 'POST',
      data: group,
    });
    yield put({
      type: `${CREATE_REPORT_GROUP}_SUCCESS`,
      payload: {
        ...action.payload,
        group: requestGroup,
      },
    });
  } catch (e) {
    yield put({ type: `${CREATE_REPORT_GROUP}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* deleteReportGroupSaga(action) {
  try {
    const { groupId } = action.payload;
    yield put({ type: `${DELETE_REPORT_GROUP}_START` });
    yield call(requestForSaga, {
      url: `/reports/groups/${groupId}`,
      method: 'DELETE',
    });
    yield put({
      type: `${DELETE_REPORT_GROUP}_SUCCESS`,
      payload: {
        groupId,
      },
    });
    yield call(requestMessage, 'success', 'groupDeleted');
  } catch (e) {
    yield put({ type: `${DELETE_REPORT_GROUP}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* saveLayoutSaga(action) {
  try {
    const { layout, layoutsDict } = action.payload;
    yield put({ type: `${SAVE_LAYOUT}_START` });
    const dashboardOverview = yield select((s) => s.get('dashboardOverview'));
    const { requestOperations } = dashboardOverview;

    // construct final array of layouts reflecting current state on UI to be stored in DB
    const layouts = Object.entries(layoutsDict).map(([key, value]) => ({
      ...value,
      rows: key === layout.slug ? layout.rows : value.rows,
    }));

    yield all(
      requestOperations.map((operation) => {
        if (operation.type === 'update') {
          const group = operation.data;
          return call(requestForSaga, {
            url: `/reports/groups/${group.id}`,
            method: 'PATCH',
            data: dissoc('id', group),
          });
        }
        return null;
      }),
    );

    yield call(requestForSaga, {
      url: '/settings/dashboard',
      method: 'PUT',
      data: layouts,
    });

    yield put({
      type: `${SAVE_LAYOUT}_SUCCESS`,
      payload: {
        layout: layout,
        layouts: layouts,
      },
    });
  } catch (e) {
    yield put({ type: `${SAVE_LAYOUT}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* renameLayoutSaga(action) {
  const { layoutTitle, layout, layouts, layoutsDict } = action.payload;

  const originalLayoutSlug = layout.slug;

  const newLayout = {
    ...layout,
    title: layoutTitle,
    slug: normalizeToLayoutSlug(layoutTitle),
  };

  // replace old layout with new layout one in the array of layouts
  const newLayouts = layouts.map((item) => {
    return item.slug === originalLayoutSlug ? newLayout : item;
  });

  const newLayoutsDict = Object.entries(layoutsDict).reduce(
    (acc, [key, value]) => {
      acc[key === originalLayoutSlug ? newLayout.slug : key] =
        key === originalLayoutSlug ? newLayout : value;
      return acc;
    },
    {},
  );

  yield put({
    type: `${RENAME_LAYOUT}_START`,
    payload: {
      layout: newLayout,
      layouts: newLayouts,
      layoutsDict: newLayoutsDict,
    },
  });

  yield put({
    type: SAVE_LAYOUT,
    payload: {
      layout: newLayout,
      layoutsDict: newLayoutsDict,
    },
  });
}

function* deleteLayoutSaga(action) {
  const { layout, layouts, layoutsDict } = action.payload;

  const newLayouts = layouts.filter((l) => l.slug !== layout.slug);

  if (layout.slug in layoutsDict) {
    delete layoutsDict[layout.slug];
  }

  const newLayout = newLayouts.length ? newLayouts[0] : initialLayout;

  yield put({
    type: `${DELETE_LAYOUT}_START`,
    payload: {
      layout: newLayout,
      layouts: newLayouts,
      layoutsDict: layoutsDict,
    },
  });

  yield put({
    type: SAVE_LAYOUT,
    payload: {
      layout: newLayout,
      layoutsDict: layoutsDict,
    },
  });
}

function* clearDashboardSaga() {
  try {
    yield put({
      type: `${CLEAR_DASHBOARD}_SUCCESS`,
    });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
function* addWidgetSaga(action) {
  const { widgetName } = action.payload;
  const dashboardOverview = yield select((s) => s.get('dashboardOverview'));
  if (isNonNullable(widgetName) && widgetName === 'DeviceChart') {
    const previousLayout = dashboardOverview.layout;
    const componentData = dashboardOverview.discoveryHistory;
    if (
      !componentData.length &&
      !hasWidgetInLayout(previousLayout, 'DeviceChart')
    ) {
      yield call(fetchDiscoveryHistory);
    }
  }
  if (isNonNullable(widgetName) && widgetName === 'NetworkVendorMix') {
    const previousLayout = dashboardOverview.layout;
    const componentData = dashboardOverview.networkVendorMix;
    if (
      !componentData.length &&
      !hasWidgetInLayout(previousLayout, 'NetworkVendorMix')
    ) {
      yield call(fetchNetworkVendorMix);
    }
  }
  if (isNonNullable(widgetName) && widgetName === 'HostVendorMix') {
    const previousLayout = dashboardOverview.layout;
    const componentData = dashboardOverview.hostVendorMix;
    if (
      !componentData.length &&
      !hasWidgetInLayout(previousLayout, 'HostVendorMix')
    ) {
      yield call(fetchHostVendorMix);
    }
  }
  if (isNonNullable(widgetName) && widgetName === 'TextRow') {
    const previousLayout = dashboardOverview.layout;
    const componentData = dashboardOverview.textRow;
    if (
      !componentData.length &&
      !hasWidgetInLayout(previousLayout, 'textRow')
    ) {
      yield call(fetchTextRow);
    }
  }
  yield put({
    type: `${ADD_WIDGET}_SUCCESS`,
    payload: action.payload,
  });
}

// Individual exports for testing
export function* defaultSaga() {
  yield all([
    takeEvery(FETCH_DASHBOARD, fetchDashboardSaga),
    takeEvery(CREATE_REPORT_GROUP, createReportGroupSaga),
    takeEvery(DELETE_REPORT_GROUP, deleteReportGroupSaga),
    takeEvery(SAVE_LAYOUT, saveLayoutSaga),
    takeEvery(CLEAR_DASHBOARD, clearDashboardSaga),
    takeEvery(FETCH_REPORTS, fetchReportsSaga),
    takeEvery(ADD_WIDGET, addWidgetSaga),
    takeEvery(RENAME_LAYOUT, renameLayoutSaga),
    takeEvery(DELETE_LAYOUT, deleteLayoutSaga),
  ]);
}

export default defaultSaga;
